import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  AlertCircle, 
  ArrowUpDown, 
  FileWarning, 
  FireExtinguisher, 
  UserRound, 
  Clock,
  CircleDashed
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { formatDateToBritish } from "@/lib/dateUtils";
import { OperationsDashboardService } from "@/services/operationsDashboardService";
import { IssuesByCategory, OperationalIssueDetail } from "@/types/operations";
import SearchFilter from "./SearchFilter";
import DateRangeFilter from "./DateRangeFilter";

const IssuesList: React.FC = () => {
  const [issuesByCategory, setIssuesByCategory] = useState<IssuesByCategory | null>(null);
  const [filteredIssues, setFilteredIssues] = useState<IssuesByCategory | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [dateRange, setDateRange] = useState<{start?: Date, end?: Date}>({});
  const navigate = useNavigate();

  useEffect(() => {
    const fetchIssues = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const data = await OperationsDashboardService.getIssuesByCategory();
        setIssuesByCategory(data);
        setFilteredIssues(data);
      } catch (err) {
        setError("Failed to load issues. Please try again later.");
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchIssues();
  }, []);

  // Apply all filters
  useEffect(() => {
    if (!issuesByCategory) return;
    
    const filterIssueList = (issues: OperationalIssueDetail[]) => {
      let filtered = [...issues];
      
      // Filter out issues from demo employers (emails containing "demo-employer")
      filtered = filtered.filter(issue => 
        !issue.employer_email.includes("demo-employer")
      );
      
      // Filter out issues created before May 20th, 2025
      const cutoffDate = new Date('2025-05-20');
      filtered = filtered.filter(issue => {
        const issueDate = new Date(issue.created_at);
        return issueDate >= cutoffDate;
      });
      
      // Apply search filter
      if (searchQuery.trim()) {
        const lowercaseQuery = searchQuery.toLowerCase();
        filtered = filtered.filter(
          (issue) =>
            issue.employer_email.toLowerCase().includes(lowercaseQuery) ||
            issue.employee_name.toLowerCase().includes(lowercaseQuery) ||
            issue.description.toLowerCase().includes(lowercaseQuery)
        );
      }
      
      // Apply date filter
      if (dateRange.start && dateRange.end) {
        filtered = filtered.filter((issue) => {
          const issueDate = new Date(issue.created_at);
          return issueDate >= dateRange.start! && issueDate <= dateRange.end!;
        });
      }
      
      return filtered;
    };

    setFilteredIssues({
      critical_blockers: {
        negative_tax: filterIssueList(issuesByCategory.critical_blockers.negative_tax),
        missing_filings: filterIssueList(issuesByCategory.critical_blockers.missing_filings),
      },
      warnings: {
        missing_nino: filterIssueList(issuesByCategory.warnings.missing_nino),
      },
    });
  }, [issuesByCategory, searchQuery, dateRange]);

  // Handle search
  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  // Handle date range filter
  const handleDateRangeChange = (start: Date | undefined, end: Date | undefined) => {
    setDateRange({ start, end });
  };

  // Navigate to issue detail page
  const handleIssueClick = (issueId: number) => {
    navigate(`/operations/issues/${issueId}`);
  };

  // Get urgency indicator
  const getUrgencyIndicator = (urgency: string) => {
    switch (urgency) {
      case "high":
        return <Badge variant="destructive" className="ml-2">High Urgency</Badge>;
      case "normal":
        return null;
      case "low":
        return <Badge variant="outline" className="ml-2">Low Urgency</Badge>;
      default:
        return null;
    }
  };

  // Issue row component
  const IssueRow = ({ issue }: { issue: OperationalIssueDetail }) => (
    <div 
      className="p-4 border-b last:border-0 hover:bg-gray-50 cursor-pointer"
      onClick={() => handleIssueClick(issue.id)}
    >
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-2">
        <div className="flex items-center">
          <UserRound className="h-4 w-4 mr-2 text-gray-500" />
          <span className="font-medium">{issue.employer_email}</span>
          {getUrgencyIndicator(issue.urgency)}
        </div>
        <div className="mt-2 md:mt-0 text-sm text-gray-500 flex items-center">
          <Clock className="h-3 w-3 mr-1" />
          {formatDateToBritish(issue.created_at)}
        </div>
      </div>
      <p className="text-sm mb-2 line-clamp-2">{issue.description}</p>
      <div className="flex justify-between items-center mt-2">
        <div className="text-sm">
          <span className="text-gray-500 mr-2">Employee:</span>
          {issue.employee_name}
        </div>
        <Button 
          variant="outline" 
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            handleIssueClick(issue.id);
          }}
        >
          View Details
        </Button>
      </div>
    </div>
  );

  // Issue list component
  const IssueListSection = ({ 
    title, 
    icon, 
    issues,
    emptyMessage,
  }: { 
    title: string;
    icon: React.ReactNode; 
    issues: OperationalIssueDetail[];
    emptyMessage: string;
  }) => (
    <div className="mb-1">
      <div className="flex items-center space-x-2 mb-2">
        {icon}
        <h3 className="text-lg font-medium">{title}</h3>
        <Badge className="ml-auto">{issues.length}</Badge>
      </div>
      <div className="border rounded-md">
        {issues.length === 0 ? (
          <div className="p-4 text-center text-gray-500">{emptyMessage}</div>
        ) : (
          issues.map((issue) => <IssueRow key={issue.id} issue={issue} />)
        )}
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <p className="text-gray-500">Loading issues...</p>
      </div>
    );
  }

  if (error || !filteredIssues) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg">
          <p className="flex items-center">
            <AlertCircle className="w-5 h-5 mr-2" />
            {error || "Failed to load issues"}
          </p>
        </div>
      </div>
    );
  }

  // Calculate total issues
  const totalCritical = 
    filteredIssues.critical_blockers.negative_tax.length + 
    filteredIssues.critical_blockers.missing_filings.length;
  
  const totalWarnings = filteredIssues.warnings.missing_nino.length;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <CardTitle>Issues Dashboard</CardTitle>
            <div className="flex flex-col sm:flex-row gap-2">
              <DateRangeFilter onFilterChange={handleDateRangeChange} />
              <SearchFilter 
                onSearch={handleSearch} 
                placeholder="Search issues..." 
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {totalCritical === 0 && totalWarnings === 0 ? (
            <div className="text-center py-10">
              <CircleDashed className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-1">No Issues Found</h3>
              <p className="text-gray-500">
                {searchQuery || (dateRange.start && dateRange.end)
                  ? "No issues match your search criteria"
                  : "There are currently no active issues in the system"}
              </p>
            </div>
          ) : (
            <Accordion type="multiple" defaultValue={["critical-blockers", "warnings"]} className="space-y-4">
              {/* Critical Blockers Section */}
              <AccordionItem value="critical-blockers" className="border rounded-md">
                <AccordionTrigger className="px-4 py-2">
                  <div className="flex items-center">
                    <AlertCircle className="h-5 w-5 mr-2 text-red-600" />
                    <span className="font-semibold text-red-700">Critical Blockers</span>
                    <Badge variant="destructive" className="ml-2">{totalCritical}</Badge>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4">
                  <div className="space-y-4">
                    <IssueListSection 
                      title="Negative Tax Issues" 
                      icon={<ArrowUpDown className="h-5 w-5 text-red-500" />}
                      issues={filteredIssues.critical_blockers.negative_tax}
                      emptyMessage="No negative tax issues found"
                    />
                    
                    <IssueListSection 
                      title="Missing Filings Issues" 
                      icon={<FileWarning className="h-5 w-5 text-red-500" />}
                      issues={filteredIssues.critical_blockers.missing_filings}
                      emptyMessage="No missing filings issues found"
                    />
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Warnings Section */}
              <AccordionItem value="warnings" className="border rounded-md">
                <AccordionTrigger className="px-4 py-2">
                  <div className="flex items-center">
                    <FireExtinguisher className="h-5 w-5 mr-2 text-yellow-600" />
                    <span className="font-semibold text-yellow-700">Warnings</span>
                    <Badge variant="outline" className="ml-2 border-yellow-500 text-yellow-700">
                      {totalWarnings}
                    </Badge>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4">
                  <IssueListSection 
                    title="Missing NINO Issues" 
                    icon={<UserRound className="h-5 w-5 text-yellow-500" />}
                    issues={filteredIssues.warnings.missing_nino}
                    emptyMessage="No missing National Insurance number issues found"
                  />
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default IssuesList; 